<template>
  <Modal :title="title" name="PAY_INVOICES_MODAL" actions>
    <template v-slot:body>
      <v-alert
        v-if="hasNegativeValues"
        class="error lighten-4 mb-4"
        :icon="false"
        elevation="0"
      >
        <span class="d-block error--text text--darken-2">
          {{ $t('recharge.hint.negativePaymentWarning', { hosts: negativeHostsString }) }}
        </span>
      </v-alert>

      <div v-if="hasNegativeValues" class="d-flex align-center mb-4">
        <v-checkbox
          v-model="confirmedNegative"
          :disabled="loading"
          hide-details
          dense
          class="ma-0 pa-0"
          :aria-label="$t('recharge.hint.confirmProceed')"
        />
        <span class="ml-2 body-2">
          {{ $t('hostOnboarding.actions.confirm') }}
        </span>
      </div>

      <p>{{ warningMessage }}</p>

      <v-data-table
        :headers="invoiceToPayHeaders"
        :items-per-page="100"
        :items="invoices"
        hide-default-footer
        disable-sort
      >
        <template v-slot:item.total="{ item }">
          {{ catalogMonetary(item.total) }}
        </template>
      </v-data-table>

      <v-divider />

      <div class="text-right pr-4 pt-4">
        <h6 class="body-2 grey--text">
          {{ $t('hausefulInvoices.headers.total') }}
        </h6>
        <p class="font-weight-bold body-1">
          {{ total }}
        </p>
      </div>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="loading"
        elevation="0"
        color="grey"
        text
      >
        {{ $t('hausefulInvoices.actions.cancel') }}
      </v-btn>
      <v-btn
        @click="pay"
        :loading="loading"
        class="ml-5 primary"
        elevation="0"
        :disabled="disablePayButton"
      >
        {{ buttonLabel }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import { catalogMonetary } from '@/util/catalog'
import { invoiceToPayHeaders } from '../adminInvoiceHeaders'
import { parseMoney } from '@/util/money'
import Modal from '@/components/app/Modal'

export default {
  name: 'ModalPayInvoices',
  components: {
    Modal,
  },

  props: {
    invoices: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      total: 0,
      loading: false,
      confirmedNegative: false,
      title: this.$t('hausefulInvoices.modals.pay.title'),
      buttonLabel: this.$t('hausefulInvoices.actions.payInvoices'),
      warningMessage: this.$t('hausefulInvoices.modals.pay.message'),
      invoiceToPayHeaders,
      catalogMonetary,
    }
  },

  computed: {
    negativeHostsString() {
      return this.negativeHosts.map(h => h.name).join(', ')
    },
    negativeHosts() {
      const totalsByHost = this.groupTotalsByHost(this.invoices)
      return Object.values(totalsByHost).filter(h => h.total < 0)
    },
    hasNegativeValues() {
      return this.negativeHosts.length > 0
    },
    externalTransferAuthorized() {
      return this.hasNegativeValues && this.confirmedNegative
    },
    disablePayButton() {
      return (
        this.loading ||
        this.invoices.length === 0 ||
        (this.hasNegativeValues && !this.confirmedNegative)
      )
    },
  },

  watch: {
    invoices(invoices) {
      this.calculateTotal(invoices)
      this.confirmedNegative = false
    },
  },

  methods: {
    ...mapActions([
      'payInvoices',
      'fetchAdminHostPendingInvoices',
    ]),
    ...mapMutations(['closeAllModals']),

    groupTotalsByHost(invoices = []) {
      return invoices.reduce((acc, inv) => {
        const hostKey = inv.hostId ?? inv.host_id ?? inv.host?.id ?? inv.host
        if (hostKey == null) return acc

        const hostName =
          inv.host?.name ??
          inv.host_name ??
          (typeof inv.host === 'object' && inv.host?.name) ??
          `Host ${hostKey}`

        const value = parseMoney(inv.total)

        if (!acc[hostKey]) acc[hostKey] = { name: hostName, total: 0 }
        acc[hostKey].total += value
        return acc
      }, {})
    },

    calculateTotal(data) {
      const total = (Array.isArray(data) ? data : []).reduce(
        (sum, item) => sum + parseMoney(item.total),
        0
      )
      this.total = catalogMonetary(total ?? 0)
    },

    async pay() {
      if (this.hasNegativeValues && !this.confirmedNegative) return
      this.loading = true
      try {
        const payload = {
          invoice_ids: this.invoices.map(i => i.id),
          external_transfer_authorized: this.externalTransferAuthorized,
        }
        await this.payInvoices(payload)

        this.$emit('resetSelectedInvoices', [])
        await this.fetchAdminHostPendingInvoices()
        this.$emit('refresh')

        this.closeAllModals()
      } finally {
        this.loading = false
      }
    },
  },

  mounted() {
    this.calculateTotal(this.invoices)
  },
}
</script>
